import * as moment from 'moment';
import { getLocalStorage } from './localStorageWrapper';

const BLACKBEARD_ORDERS_STORAGE_KEY = 'ordersRevamp';
export const OPT_IN_SNOOZED_STORAGE_KEY = 'ordersRevampOptInDismissed';
export const OPT_OUT_SNOOZED_STORAGE_KEY = 'ordersRevampOptOutDismissed';

type OptInOutFlagValues = 'initialOptIn' | 'initialOptOut' | 'disabled';
export type BlackbeardOrdersOptInOutValue = 'optIn' | 'optOut';

// We can test this file by mocking the import of `getLocalStorage`
const ls = getLocalStorage();

// Returning a boolean so changes to BlackbeardOrdersOptInOutValue create compiler errors until handled here
function removeOptInOptOutSnooze(optType: BlackbeardOrdersOptInOutValue): boolean {
  switch (optType) {
    case 'optIn': {
      ls.removeItem(OPT_IN_SNOOZED_STORAGE_KEY);
      return true;
    }
    case 'optOut': {
      ls.removeItem(OPT_OUT_SNOOZED_STORAGE_KEY);
      return true;
    }
  }
}

export function getOptInOutDismissed(type: 'optIn' | 'optOut'): Date | undefined {
  const storedDateValue = ls.getItem(
    type === 'optIn' ? OPT_IN_SNOOZED_STORAGE_KEY : OPT_OUT_SNOOZED_STORAGE_KEY,
  );
  if (!storedDateValue) {
    return;
  }

  try {
    const parsedValue = new Date(storedDateValue);
    return parsedValue;
  } catch {
    return;
  }
}

function snoozedRecently(startingDate: Date, dismissedDate?: Date) {
  if (!dismissedDate) {
    return false;
  }

  const sevenDaysAgo = moment(startingDate).subtract(7, 'days');
  const dismissedMoment = moment(dismissedDate);
  const output = 7 - dismissedMoment.diff(sevenDaysAgo, 'days');

  // if seven days prior to now is larger than the dismissed date,
  return output < 7;
}

function optInOutEnabled(
  value: OptInOutFlagValues,
): value is Exclude<OptInOutFlagValues, 'disabled'> {
  return !!value && value !== 'disabled';
}

export function snoozedOptInPrompt(optInOutType: 'optIn', startingDate: Date): boolean {
  const value = getOptInOutDismissed(optInOutType);
  return snoozedRecently(startingDate, value);
}

export function clearOptInOutSnooze() {
  removeOptInOptOutSnooze('optIn');
  removeOptInOptOutSnooze('optOut');
}

export function setBlackbeardOrdersStorageValue(newValue: BlackbeardOrdersOptInOutValue) {
  // set the optIn/optOut ls to the new value
  ls.setItem(BLACKBEARD_ORDERS_STORAGE_KEY, newValue);
}

export function getBlackbeardOrdersStorageValue(): BlackbeardOrdersOptInOutValue | undefined {
  return ls.getItem(BLACKBEARD_ORDERS_STORAGE_KEY) as BlackbeardOrdersOptInOutValue | undefined;
}

/**
 * The function first checks if the initialOptInOptOut is enabled by calling the optInOutEnabled function. If it is enabled, the function then checks the optInOutValue:
 * If optInOutValue is 'optIn', the function returns true, meaning the Blackbeard Orders should be shown.
 * If optInOutValue is 'optOut', the function returns false, meaning the Blackbeard Orders should not be shown.
 * If optInOutValue is neither 'optIn' nor 'optOut' (or not provided), the function checks if initialOptInOptOut is 'initialOptIn'. If it is, the function returns true, otherwise it returns false.
 * If initialOptInOptOut is not enabled, the function returns the boolean value of ordersRedesign.
 * @param ordersRedesign // orders_redesign feature flag value
 * @param initialOptInOptOut // orders_redesign_with_opt_in_opt_out feature flag value
 * @param optInOutValue // localStorage value
 * @returns boolean
 */
export function shouldShowBlackbeardOrders(
  ordersRedesign: boolean, // orders-redesign value
  initialOptInOptOut: 'initialOptIn' | 'initialOptOut' | 'disabled', // orders-redesign-with-opt-in-opt-out value
  optInOutValue?: BlackbeardOrdersOptInOutValue, //localStorage value
) {
  if (optInOutEnabled(initialOptInOptOut)) {
    switch (optInOutValue) {
      case 'optIn': {
        return true;
      }
      case 'optOut': {
        return false;
      }
      default: {
        // when the default value is optOut and they have not made a choice, it means they should see the new page
        // If the option is optIn, it means they should see the old page
        return initialOptInOptOut === 'initialOptIn';
      }
    }
  }

  return !!ordersRedesign;
}

export function storeSnoozeValue(optInOutType: 'optIn' | 'optOut') {
  switch (optInOutType) {
    case 'optIn': {
      ls.setItem(OPT_IN_SNOOZED_STORAGE_KEY, new Date().toISOString());
      break;
    }
    case 'optOut': {
      ls.setItem(OPT_OUT_SNOOZED_STORAGE_KEY, new Date().toISOString());
      break;
    }
  }
}
